import React, { useMemo, useState } from 'react';
import {
  Box,
  ContentTemplate,
  Grid,
  IconButton,
  Image,
  SectionHeader,
  Typography,
  WidthGuard,
} from 'web-library';
import SeabHomeImage from 'web-library/stories/assets/images/seab_homepage_image.svg';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import Blank from '../../components/Blank';
import { selectIsUserLoggedIn } from '../../store/appSlice';
import { useUserDetails } from '../../hooks/userDetails';
import ResultListing from '../../modules/result/@candidate/ResultListing';
import { candidateService } from '../../api';
import { ResultNavigationContext } from '../../routes/ResultRoute';
import RouteKey from '../../routes/key';
import { Role } from '../../models';
import TwoCubes from '../../assets/bg_size_lg.svg';
import HandWave from '../../assets/handwave.svg';
import ExamRegistrationsListingHomePage from './ExamRegistrationsListingHomePage';
import GroupExamRegistrationsListingHomePage from './GroupExamRegistrationsListingHomePage';
import NoticeBoard from '../announcement/NoticeBoard';
import AnnouncementContent from '../../modules/announcement/components/AnnouncementContent';
import UpcomingSubjectPapersBoard from '../subject/upcomingSubjectPapersBoard';

const { myExaminations, myExaminationsResults, coordinatorResultRelease, coordinatorResultList } =
  RouteKey.result.child;
const { result } = RouteKey.child;

const useStyles = makeStyles()((theme) =>
({
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    [theme.breakpoints.down('lg')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
  },
  textHeader: {
    color: theme.color.text.textHeader,
    width: 'calc(100% - 88px - 24px)',
    overflowWrap: 'break-word',
  },
  leftContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    zIndex: 3,
  },
  contentContainer: {
    marginTop: theme.spacing(8),
    position: 'relative',
    zIndex: 3,
  },
  bottomContainer: {
    width: '100%',
    maxWidth: 1920,
    paddingTop: theme.spacing(6),
    paddingLeft: theme.spacing(7),
    paddingRight: theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(4),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(3),
      paddingLeft: theme.spacing(2),
    },
    zIndex: 2,
  },
  rightContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    flex: 1,
    zIndex: 3,
    marginBottom: theme.spacing(0.4),
  },
  illustration: {
    alignItems: 'flex-end',
    right: 0,
    bottom: 0,
    width: 657,
    [theme.breakpoints.down('md')]: {
      width: 404,
    },
    [theme.breakpoints.down('sm')]: {
      width: 304,
    },
  },
  handWave: {
    marginRight: theme.spacing(3),
    width: '88px',
  },
  twoCubes: {
    position: 'absolute',
    right: 0,
    zIndex: 0,
    width: 386,
    [theme.breakpoints.down('md')]: {
      width: 254,
    },
    [theme.breakpoints.down('sm')]: {
      width: 154,
    },
  },
  examDate: {
    textDecoration: 'underline',
  },
  examRegistrationListing: {
    marginTop: theme.spacing(5),
  },
  iconContainer: {
    display: 'none',
  },
  icon: {
    width: '80px',
    height: '80px',
  },
}));
const useRole = () => {
  const { data: userData, isSuccess } = useUserDetails();
  const loggedIn = useSelector(selectIsUserLoggedIn);

  const displayName = useMemo(() => {
    if (!loggedIn) {
      return null;
    }
    if (isSuccess && userData) {
      return userData.data.displayName;
    }
    return null;
  }, [userData, isSuccess, loggedIn]);

  let detail = '';
  let accRole = '';

  if (isSuccess && userData) {
    if (userData.data.roles?.includes(Role.candidate)) {
      detail = `/${result}/${myExaminations.route}/${myExaminationsResults.route}`;
      accRole = 'candidate';
    }
    if (userData.data.roles?.includes(Role.coordinator)) {
      detail = `/${result}/${coordinatorResultList.route}/${coordinatorResultRelease.route}`;
      accRole = 'coordinator';
    }
    if (
      userData.data.roles?.includes(Role.opsAdmin) ||
      userData.data.roles?.includes(Role.sysAdmin)
    ) {
      detail = '';
      accRole = 'admin';
    }
  }

  return {
    displayName,
    detail,
    accRole,
  };
};

const Homepage = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  let resultData;
  let errorData;

  const { displayName, detail, accRole } = useRole();

  if (accRole !== 'admin') {
    const {
      data,
      error,
      isSuccess: resultSuccess,
    } = candidateService.result.candidate.useExamResultsListingQuery() || [];
    if (error) {
      errorData = error;
    }
    if (resultSuccess && data) {
      resultData = data;
    }
  }

  const [openAnnouncementModal, setOpenAnnouncementModal] = useState<boolean>(false);

  const navigationValue = useMemo(
    () => ({
      toList: ``,
      toDetail: detail,
      toBlankPage: ``,
    }),
    [detail]
  );

  return (
    (<ResultNavigationContext.Provider value={navigationValue}>
      <Image className={classes.twoCubes} src={TwoCubes} alt="cube pic" />
      <ContentTemplate type="full" transparent>
        <Box className={classes.container}>
          <Box className={classes.leftContainer}>
            <Image className={classes.handWave} src={HandWave} alt="hand wave" />
            <Typography className={classes.textHeader} variant="h1">
              {`Welcome, ${displayName}`}
            </Typography>
          </Box>
        </Box>
        {accRole !== 'admin' && (
          <Box className={classes.contentContainer}>
            <NoticeBoard />

            {accRole === 'candidate' && (
              <Box className={classes.examRegistrationListing}>
                <UpcomingSubjectPapersBoard />
              </Box>
            )}

            {resultData && resultData.length > 0 && (
              <Box className={classes.examRegistrationListing}>
                <ResultListing userRole={accRole} header="Results" />
              </Box>
            )}

            {accRole === 'candidate' && (
              <Box className={classes.examRegistrationListing}>
                <ExamRegistrationsListingHomePage />
              </Box>
            )}

            {accRole === 'coordinator' && (
              <Box className={classes.examRegistrationListing}>
                {/*<GroupExamRegistrationsListingHomePage />*/}
              </Box>
            )}

            {errorData && errorData.response?.status === 403 && accRole !== 'admin' && (
              <Blank
                content="We’re sorry, but you do not have access to this feature."
                header="View Results"
              />
            )}
            <AnnouncementContent
              openAnnouncementModal={openAnnouncementModal}
              setOpenAnnouncementModal={setOpenAnnouncementModal}
              isPriorityNotice
              announcementType="ANNOUNCEMENTS"
            />
          </Box>
        )}
      </ContentTemplate>
      <WidthGuard backgroundColor={theme.color.background.bgMain}>
        <Box className={classes.bottomContainer}>
          <SectionHeader
            className={classes.iconContainer}
            title={t('admin->>HomePage.QuickLinks')}
          />
          <Grid container direction="row" alignItems="flex-start" spacing={1}>
            <Box className={classes.iconContainer}>
              <Grid xs={6} sm={4} md={3} lg={2}>
                <IconButton
                  className={classes.icon}
                  ariaLabel="training"
                  contained
                  color=""
                  iconSize="XL"
                  label={t('admin->>HomePage.ExamTimeable')}
                  name={IconName.training} />
              </Grid>
              <Grid xs={6} sm={4} md={3} lg={2}>
                <IconButton
                  className={classes.icon}
                  ariaLabel="warning"
                  contained
                  color=""
                  iconSize="XL"
                  label={t('admin->>HomePage.Syllabuses')}
                  name={IconName.warning} />
              </Grid>
              <Grid xs={3} sm={3} md={2} lg={1}>
                <IconButton
                  className={classes.icon}
                  ariaLabel="declare"
                  contained
                  color=""
                  iconSize="XL"
                  label={t('admin->>HomePage.RulesAndRegulations')}
                  name={IconName.declare} />
              </Grid>
            </Box>

            <Box className={classes.rightContainer}>
              <img className={classes.illustration} alt="homepage" src={SeabHomeImage} />
            </Box>
          </Grid>
        </Box>
      </WidthGuard>
    </ResultNavigationContext.Provider>)
  );
};

export default Homepage;
